import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSave, MdPerson, MdCalendarToday, MdOutlineLocalShipping } from 'react-icons/md';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../lib/utils';

const BulkOrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/bulk-orders/${id}`);
        setOrder(response.data.data);
        setStatus(response.data.data.status);
        setAdminNotes(response.data.data.adminNotes || '');
      } catch (error) {
        console.error('Error fetching bulk order details:', error);
        toast.error('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await api.put(`/bulk-orders/${id}/status`, { status, adminNotes });
      toast.success('Order status updated successfully');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-ucla-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Order Not Found</h2>
        <button onClick={() => navigate('/admin/bulk-orders')} className="mt-4 text-admin-ucla-600 hover:underline">
          Return to Bulk Orders
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/admin/bulk-orders')}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <MdArrowBack className="mr-1" /> Back to Bulk Orders
        </button>
      </div>

      <div className="sm:flex sm:items-center sm:justify-between mb-6 mt-4">
        <div>
          <h1 className="text-2xl font-bold text-admin-slate-900 dark:text-admin-slate-100">
            Bulk Order #{order._id.substring(order._id.length - 6)}
          </h1>
          <p className="mt-1 text-sm text-admin-slate-600 dark:text-admin-slate-400">
            Review uploaded image and update processing status
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Image Viewer */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="font-semibold text-gray-800">Order List Image</h3>
              <button
                onClick={() => setIsZoomed(!isZoomed)}
                className="text-sm bg-white border border-gray-200 px-3 py-1 rounded hover:bg-gray-50"
              >
                {isZoomed ? 'Zoom Out' : 'Zoom In'}
              </button>
            </div>
            <div className={`bg-gray-100 p-4 flex justify-center overflow-auto ${isZoomed ? 'h-[800px]' : 'h-[500px]'}`}>
              <img
                src={getImageUrl(order.imagePath)}
                alt="Order List"
                className={`transition-all duration-300 ${isZoomed ? 'w-auto max-w-none cursor-zoom-out object-none' : 'w-full h-full object-contain cursor-zoom-in'}`}
                onClick={() => setIsZoomed(!isZoomed)}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-800 mb-2">Customer Notes</h3>
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 min-h-[100px] whitespace-pre-wrap border border-gray-100">
              {order.customerNotes || <span className="text-gray-400 italic">No notes provided by the customer.</span>}
            </div>
          </div>
        </div>

        {/* Right Column - Controls & Info */}
        <div className="space-y-6">
          {/* Action Panel */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-800 mb-4 border-b pb-2">Order Processing</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:ring-1 focus:ring-admin-ucla-500"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Internal Notes</label>
                <textarea
                  className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:ring-1 focus:ring-admin-ucla-500 min-h-[120px]"
                  placeholder="Notes for internal use only (e.g., pending items, contacted customer, etc.)"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                />
              </div>

              <button
                onClick={handleSave}
                disabled={isSaving}
                className="w-full bg-admin-ucla-600 hover:bg-admin-ucla-700 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center justify-center disabled:opacity-70"
              >
                {isSaving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                ) : (
                  <MdSave className="mr-2 h-5 w-5" />
                )}
                Save Changes
              </button>
            </div>
          </div>

          {/* Customer Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-800 mb-4 border-b pb-2">Customer Information</h3>

            <div className="space-y-4">
              <div className="flex items-start">
                <MdPerson className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {order.user ? order.user.name : 'Guest User'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {order.user ? order.user.email : order.guestContactInfo}
                  </p>
                  {order.user && order.user.phone && (
                    <p className="text-xs text-gray-500">{order.user.phone}</p>
                  )}
                </div>
              </div>

              {(order.phone || order.address) && (
                <div className="flex items-start">
                  <MdOutlineLocalShipping className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Delivery Details</p>
                    {order.phone && (
                      <p className="text-xs text-gray-500">{order.phone}</p>
                    )}
                    {order.address && (
                      <p className="text-xs text-gray-500">
                        {order.address}{order.city ? `, ${order.city}` : ''}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-start">
                <MdCalendarToday className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Submitted On</p>
                  <p className="text-xs text-gray-500">
                    {new Date(order.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Help Box */}
          <div className="bg-admin-ucla-50 rounded-xl p-5 border border-admin-ucla-100">
            <h4 className="font-medium text-admin-ucla-800 mb-2 flex items-center">
              <MdOutlineLocalShipping className="mr-2" /> How to process?
            </h4>
            <p className="text-xs text-admin-ucla-700 leading-relaxed">
              Review the uploaded list. If everything is clear, contact the customer to confirm the total amount and finalize the order. Once the real order is created or shipped, mark this as <strong>Completed</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkOrderDetailPage;
